import { api } from "@/shared/lib/api";
import { z } from "zod";
import type {
  MatchDisputeResponse,
  MatchDisputeListItemDto,
  CreateMatchDisputeRequest,
  ResolveMatchDisputeRequest,
  RejectAllMatchDisputesRequest,
  RejectAllMatchDisputesResponse,
} from "../model/types";

const disputeStatusSchema = z.enum(["Open", "InReview", "Resolved", "Rejected"]);
const resolutionTypeSchema = z.enum(["Uphold", "Overturn", "Reject"]);
const evidenceSchema = z.object({
  id: z.string(),
  urlOrStoragePath: z.string(),
  evidenceType: z.string(),
  uploadedAtUtc: z.string().datetime({ offset: true }),
});
const disputeListItemSchema = z.object({
  id: z.string(),
  matchId: z.string(),
  tournamentId: z.string(),
  teamId: z.string(),
  teamName: z.string().nullable().optional(),
  status: disputeStatusSchema,
  reason: z.string(),
  createdAtUtc: z.string().datetime({ offset: true }),
  resolvedAtUtc: z.string().datetime({ offset: true }).nullable().optional(),
  isSystemConflict: z.boolean(),
});
const disputeResponseSchema = disputeListItemSchema.extend({
  createdByUserId: z.string(),
  createdByDisplayName: z.string().nullable().optional(),
  resolvedByUserId: z.string().nullable().optional(),
  resolvedByDisplayName: z.string().nullable().optional(),
  resolutionType: resolutionTypeSchema.nullable().optional(),
  resolutionComment: z.string().nullable().optional(),
  evidence: z.array(evidenceSchema),
});
const rejectAllResponseSchema = z.object({ closedCount: z.number().int() });

export async function fetchMatchDisputesByMatch(
  matchId: string
): Promise<MatchDisputeListItemDto[]> {
  const { data } = await api.get<unknown>(
    "/matchdisputes",
    { params: { matchId } }
  );
  return z.array(disputeListItemSchema).parse(data);
}

export async function fetchMatchDisputesByTournament(
  tournamentId: string
): Promise<MatchDisputeListItemDto[]> {
  const { data } = await api.get<unknown>(
    "/matchdisputes",
    { params: { tournamentId } }
  );
  return z.array(disputeListItemSchema).parse(data);
}

export async function fetchMatchDisputeById(
  id: string
): Promise<MatchDisputeResponse | null> {
  const { data } = await api.get<unknown>(`/matchdisputes/${id}`);
  return disputeResponseSchema.parse(data);
}

export async function createMatchDispute(
  body: CreateMatchDisputeRequest
): Promise<MatchDisputeResponse> {
  const { data } = await api.post<unknown>("/matchdisputes", body);
  return disputeResponseSchema.parse(data);
}

/**
 * KAN-171: массово отклонить неразобранные диспуты турнира.
 * Бэкенд отобьёт 400, если среди них есть системные конфликты (WIN+WIN) —
 * их разбирают поштучно.
 */
export async function rejectAllMatchDisputes(
  tournamentId: string,
  body: RejectAllMatchDisputesRequest
): Promise<RejectAllMatchDisputesResponse> {
  const { data } = await api.post<unknown>(
    `/matchdisputes/tournament/${tournamentId}/reject-all`,
    body
  );
  return rejectAllResponseSchema.parse(data);
}

export async function resolveMatchDispute(
  id: string,
  body: ResolveMatchDisputeRequest
): Promise<MatchDisputeResponse> {
  const { data } = await api.put<unknown>(
    `/matchdisputes/${id}/resolve`,
    body
  );
  return disputeResponseSchema.parse(data);
}
